import { Fragment, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { Actions, Link, Modal, Text } from '@gasbuddy/react-components';
import { useTranslation } from 'react-i18next';
import EditMembernameStatus, { USERNAME_STATE } from '../EditMemberNameStatus';
import { noop } from '../../../lib/utils';

export const UPDATE_MEMBER_NAME = gql`
  mutation UpdateMemberName($memberName: String!) {
    updateMemberName(memberName: $memberName) {
      canSetMemberName
      memberName
    }
  }
`;

export default function EditMemberNameModal({
  memberName: generatedMemberName,
  messageType,
  onCloseModal,
  onMemberNameChangeRequested,
  onMemberNameUpdated,
  showModal,
  statusVariant,
}) {
  const { t } = useTranslation(['account', 'miscellaneous']);
  const [hasRequestedMemberNameChange, setHasRequestedMemberNameChange] = useState(false);
  const [hasChangedMemberName, setHasChangedMemberName] = useState(false);
  const [updateMemberName, { loading: isLoading, error }] = useMutation(UPDATE_MEMBER_NAME);
  const actionText = messageType === 'both' ? 'on GasBuddy' : `this ${messageType}`;

  const onEditMemberName = useCallback(() => {
    setHasRequestedMemberNameChange(true);
    onMemberNameChangeRequested();
  }, [onMemberNameChangeRequested]);

  const handleSubmit = useCallback((newMemberName) => {
    updateMemberName({
      variables: {
        memberName: newMemberName,
      },
      onCompleted: () => {
        onMemberNameUpdated();
        setHasChangedMemberName(true);
      },
    });
  }, [updateMemberName, onMemberNameUpdated]);

  return (
    <Modal
      forceIsShowing={showModal}
      size="sm"
      bgClickable={false}
      closeIcon={false}
      onClose={onCloseModal}
      content={({ close }) => {
        if (!hasRequestedMemberNameChange && statusVariant !== USERNAME_STATE.CUSTOMIZED) {
          return (
            <Fragment>
              <Text as="p">{t('postingAs', { ns: 'account', context: actionText })}</Text>
              <Text as="p" bold>{generatedMemberName}</Text>
              <Text as="p">{t('changeUsername', { ns: 'account' })}</Text>
              <br />
              <Actions>
                <Actions.Button
                  primary
                  wide
                  onClick={close}
                  desktop={6}
                >
                  {t('notNow', { ns: 'account' })}
                </Actions.Button>
                <Actions.Action desktop={6}>
                  <Link onClick={onEditMemberName} uppercase>
                    {t('editUsername', { ns: 'account' })}
                  </Link>
                </Actions.Action>
              </Actions>
            </Fragment>
          );
        }

        return (
          <EditMembernameStatus
            variant={hasChangedMemberName ? USERNAME_STATE.UPDATED : statusVariant}
            onClose={close}
            onSubmit={handleSubmit}
            error={error && t('usernameUpdateError', { ns: 'account' })}
            isLoading={isLoading}
            memberName={generatedMemberName}
          />
        );
      }}
    />
  );
}

EditMemberNameModal.propTypes = {
  memberName: PropTypes.string.isRequired,
  messageType: PropTypes.oneOf(['review', 'price', 'both']).isRequired,
  onCloseModal: PropTypes.func,
  onMemberNameChangeRequested: PropTypes.func,
  onMemberNameUpdated: PropTypes.func,
  showModal: PropTypes.bool,
  statusVariant: PropTypes.number,
};

EditMemberNameModal.defaultProps = {
  onCloseModal: noop,
  onMemberNameChangeRequested: noop,
  onMemberNameUpdated: noop,
  showModal: false,
  statusVariant: USERNAME_STATE.EDITABLE,
};
